"use client";

import * as React from "react";
import { Line, LineChart, CartesianGrid, XAxis, YAxis } from "recharts";

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../components/ui/Card";
import {
    ChartContainer,
    ChartLegend,
    ChartLegendContent,
    ChartTooltip,
    ChartTooltipContent,
} from "../../components/ui/chart";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/Select";

// const chartConfig = {
//     visitors: { label: "Visitors" },
//     leaving: { label: "יוצאים", color: "hsl(var(--chart-1))" },
//     entering: { label: "נכנסים", color: "hsl(var(--chart-2))" },
// };

// const locationHebrewName = {
//     "AHAD_HA'AM": "אחד העם",
//     "SAN_MARTIN": "סן מרטין",
//     "ZVI": "שדרות צבי",
//     "BERNSTEIN": "פרץ ברנשטיין",
// }

const IL_TZ = "Asia/Jerusalem";

const CustomXAxisTick = ({ x, y, payload }) => {
    if (!payload || !payload.value) return null;
    const date = new Date(payload.value);
    if (isNaN(date.getTime())) return null;

    const hourStr = date.toLocaleTimeString("he-IL", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: IL_TZ,
    });

    const dayDateStr = date.toLocaleDateString("he-IL", {
        weekday: "short",
        day: "numeric",
        month: "numeric",
        timeZone: IL_TZ,
    });

    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={12}
                textAnchor="middle"
                fill="#374151"
                fontSize={11}
                fontWeight="600"
            >
                {hourStr}
            </text>
            <text
                x={0}
                y={0}
                dy={25}
                textAnchor="middle"
                fill="#6b7280"
                fontSize={10}
            >
                {dayDateStr}
            </text>
        </g>
    );
};

export const ShadLineChart = React.memo(function Component({ data, deviceNames, lineDeviceName, setLineDeviceName, filterDayOptions, setTimeRange, timeRange, filteredData, chartDescription, chartConfig, chartContainerStyle="aspect-auto h-[303px] w-full" }) {
    const sanitizedData = React.useMemo(() => {
        if (!filteredData || !Array.isArray(filteredData)) return [];
        return filteredData.map(item => {
            const newItem = { ...item };
            Object.keys(chartConfig).forEach(key => {
                if (newItem[key] !== undefined && typeof newItem[key] === "number") {
                    newItem[key] = Math.max(0, newItem[key]);
                }
            });
            return newItem;
        });
    }, [filteredData, chartConfig]);

    return (
        <Card className="h-full flex flex-col justify-between">
            <CardHeader className="flex items-center gap-4 space-y-0 justify-between border-b py-3 flex-row min-h-[73px]">
                <div className="flex flex-col sm:flex-row items-center gap-2">
                    <Select value={timeRange} onValueChange={setTimeRange}>
                        <SelectTrigger
                            className="h-7 w-[150px] rounded-lg pl-2.5"
                            aria-label="Select a value"
                        >
                            <SelectValue placeholder="Last 6 months" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                            {filterDayOptions.map(({ value, filterDayOptionComponent }, i) => (
                                <SelectItem value={value} key={i} className="rounded-lg">
                                    <div className="flex flex-row gap-1">
                                        {filterDayOptionComponent}
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {deviceNames && deviceNames.length > 1 && (
                        <Select value={lineDeviceName} onValueChange={setLineDeviceName}>
                            <SelectTrigger
                                className="h-7 w-[130px] rounded-lg pl-2.5"
                                aria-label="Select a value"
                            >
                                <SelectValue placeholder="Select location" />
                            </SelectTrigger>
                            <SelectContent align="end" className="rounded-xl">
                                {deviceNames.map((key) => (
                                    <SelectItem key={key} value={key} className="rounded-lg [&_span]:flex">
                                        <div className="flex items-center gap-2 text-xs">
                                            {key}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                </div>

                <div className="grid gap-1 text-right">
                    <CardTitle>{lineDeviceName}</CardTitle>
                    <CardDescription>{chartDescription}</CardDescription>
                </div>
            </CardHeader>

            <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6 flex-1 flex flex-col justify-center">
                <ChartContainer
                    config={chartConfig}
                    className={`mx-auto w-full ${chartContainerStyle}`}
                >
                    <LineChart data={sanitizedData} margin={{ top: 10, right: 12, left: 0, bottom: 5 }}>
                        <CartesianGrid vertical={false} />
                        <YAxis
                            domain={[0, 'auto']}
                            allowDataOverflow={false}
                            tickLine={false}
                            axisLine={false}
                            tickMargin={6}
                            width={36}
                            tick={{ fontSize: 10, fill: "#6b7280" }}
                            tickFormatter={(value) => Number(value).toLocaleString()}
                        />
                        <XAxis
                            dataKey="date"
                            tickLine={false}
                            axisLine={false}
                            tick={<CustomXAxisTick />}
                            interval="preserveStartEnd"
                            minTickGap={45}
                            height={38}
                        />
                        <ChartTooltip
                            cursor={false}
                            content={
                                <ChartTooltipContent
                                    labelFormatter={(value) => {
                                        const d = new Date(value);
                                        if (isNaN(d.getTime())) return value;
                                        const hour = d.toLocaleTimeString("he-IL", {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            hour12: false,
                                            timeZone: IL_TZ,
                                        });
                                        const dayDate = d.toLocaleDateString("he-IL", {
                                            weekday: "long",
                                            day: "numeric",
                                            month: "long",
                                            timeZone: IL_TZ,
                                        });
                                        return `${dayDate} (${hour})`;
                                    }}
                                    indicator="dot"
                                />
                            }
                        />
                        {
                            Object.keys(chartConfig).map(c=><Line
                                key={c}
                                dataKey={c}
                                type="monotone"
                                stroke={`var(--color-${c})`}
                                strokeWidth={2}
                                dot={false}
                                strokeDasharray={c==="occupancy"?"5 5":""}
                            />)
                        }
                        <ChartLegend content={<ChartLegendContent />} />
                    </LineChart>
                </ChartContainer>
            </CardContent>
        </Card>
    );
});